import React, { useEffect } from 'react';
import { Receipt } from '../types';
import { formatDate } from '../services/utils';

interface DuplicateReceiptModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onSkip: () => void;
  existingReceipt: Receipt;
  newReceipt: Receipt;
}

const ComparisonRow: React.FC<{ label: string; oldValue: React.ReactNode; newValue: React.ReactNode }> = ({ label, oldValue, newValue }) => (
    <div className="grid grid-cols-3 gap-2 py-2 border-b">
        <div className="font-semibold text-gray-500 text-sm col-span-1">{label}</div>
        <div className="text-gray-800 text-sm col-span-1">{oldValue}</div>
        <div className="text-green-700 font-medium text-sm col-span-1">{newValue}</div>
    </div>
);

export const DuplicateReceiptModal: React.FC<DuplicateReceiptModalProps> = ({ isOpen, onConfirm, onSkip, existingReceipt, newReceipt }) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onSkip();
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        onConfirm();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onSkip, onConfirm]);

  if (!isOpen) {
    return null;
  }
  
  const formatAmount = (amount?: number, currencyName?: string) => {
      if (amount === undefined || amount === null) return 'N/A';
      return `${amount.toLocaleString('cs-CZ')} ${currencyName || ''}`;
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" 
      onClick={onSkip}
      role="dialog"
      aria-modal="true"
      aria-labelledby="duplicate-modal-title"
    >
      <div 
        className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-2xl w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center pb-3 border-b">
          <h2 id="duplicate-modal-title" className="text-xl font-bold text-gray-800">Detekována duplicitní účtenka</h2>
          <button 
            onClick={onSkip} 
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Zavřít"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="my-4">
            <p className="text-gray-700 mb-4">Soubor <strong>{(newReceipt.fileBlob as File)?.name || newReceipt.fileName}</strong> již v databázi existuje. Chcete přepsat stávající záznam novými daty?</p>

            <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-3 gap-2 mb-2">
                    <div className="font-bold text-gray-600 text-sm col-span-1">Položka</div>
                    <div className="font-bold text-gray-600 text-sm col-span-1">Stávající hodnota</div>
                    <div className="font-bold text-green-800 text-sm col-span-1">Nová hodnota</div>
                </div>
                <ComparisonRow label="Datum" oldValue={formatDate(existingReceipt.receiptDate)} newValue={formatDate(newReceipt.receiptDate)} />
                <ComparisonRow label="Podnik" oldValue={existingReceipt.businessName || 'N/A'} newValue={newReceipt.businessName || 'N/A'} />
                <ComparisonRow label="Částka" oldValue={formatAmount(existingReceipt.amount, existingReceipt.currency.name)} newValue={formatAmount(newReceipt.amount, newReceipt.currency.name)} />
                <ComparisonRow label="Kategorie" oldValue={existingReceipt.category.name} newValue={newReceipt.category.name} />
            </div>
        </div>

        <div className="mt-auto pt-4 border-t flex justify-end gap-3">
          <button 
            onClick={onSkip} 
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            Přeskočit
          </button>
          <button 
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Přepsat stávající
          </button>
        </div>
      </div>
    </div>
  );
};